import { useMemo, useState } from 'react';

import type { ContentBlockWarningsItem } from '@nucleus-commerce/api-client/admin';

import { BlockPreviewPanel } from '../preview/block-preview-panel';

import { ActiveSlideContext } from './active-slide';
import { BannerEditor } from './banner-editor';
import { BlockEditorShell, type BlockEditorShellProps } from './block-editor-shell';
import { RailEditor } from './rail-editor';

export interface BlockEditorProps extends Omit<BlockEditorShellProps, 'children' | 'preview'> {
  /** Null edits a shared block of the organisation. */
  storeId: string | null;
  /** Read-time warnings of the saved block; slides use them for their warning dot. */
  warnings?: readonly ContentBlockWarningsItem[];
  /** Publication of the saved rail products in this store; see RailProductsList. */
  publication?: ReadonlyMap<string, boolean>;
}

/**
 * The shell with the editor that matches `defaultValues.kind` and the live preview in its
 * preview slot. The expanded slide card and the preview's current slide are one state.
 */
export function BlockEditor({ storeId, warnings, publication, ...shell }: BlockEditorProps) {
  const [active, setActive] = useState<number | null>(0);
  const activeSlide = useMemo(() => ({ active, setActive }), [active]);
  return (
    <ActiveSlideContext.Provider value={activeSlide}>
      <BlockEditorShell {...shell} preview={<BlockPreviewPanel storeId={storeId} />}>
        {shell.defaultValues.kind === 'banner' ? (
          <BannerEditor storeId={storeId} warnings={warnings} />
        ) : (
          <RailEditor storeId={storeId} publication={publication} />
        )}
      </BlockEditorShell>
    </ActiveSlideContext.Provider>
  );
}
